import { VALUE_CODES, ValueCode } from "../models/OverviewDashboard";
import { ValueHistorySeries } from "../models/ValueHistory";
import { UpCareStat } from "../utils/UpCareStats";
import {
    formatStatisticsMonthLabel,
    getCalendarMonthPeriods,
    StatisticsPeriod
} from "../utils/StatisticsPeriod";
import ValueMeasureService from "./MeasureService";

export interface ValueHistoryServiceConfig {
    fhirBaseUrl: string;
    measureId: string;
}

export interface ValueHistoryQuery {
    organizationReferences: string[];
    months?: number;
    referenceDate?: Date;
}

const STAT_IDS: Record<ValueCode, string> = {
    bienveillance: "average-bienveillance",
    respect: "average-respect",
    "faire-equipe": "average-faire-equipe",
    engagement: "average-engagement"
};

const evaluationCache = new Map<string, Promise<UpCareStat[]>>();

class ValueHistoryService {
    private fhirBaseUrl: string;
    private measureId: string;

    constructor(config: ValueHistoryServiceConfig) {
        this.fhirBaseUrl = config.fhirBaseUrl;
        this.measureId = config.measureId;
    }

    async load(query: ValueHistoryQuery): Promise<ValueHistorySeries> {
        const periods = getCalendarMonthPeriods(
            query.months ?? 6,
            query.referenceDate ?? new Date()
        );
        const organizationReferences = Array.from(new Set(query.organizationReferences.filter(Boolean)));

        if (organizationReferences.length === 0) {
            return periods.map((period) => this.emptyPoint(period));
        }

        return Promise.all(
            periods.map((period) => this.loadPeriod(period, organizationReferences))
        );
    }

    private async loadPeriod(
        period: StatisticsPeriod,
        organizationReferences: string[]
    ) {
        const results = await Promise.allSettled(
            organizationReferences.map((reference) => this.evaluate(reference, period))
        );

        const fulfilled = results
            .filter((result): result is PromiseFulfilledResult<UpCareStat[]> => result.status === "fulfilled")
            .map((result) => result.value);

        const values = VALUE_CODES.reduce((acc, code) => {
            const available = fulfilled
                .map((stats) => this.getPercentage(stats, code))
                .filter((value): value is number => value !== undefined && Number.isFinite(value));

            acc[code] = available.length > 0
                ? Math.round(available.reduce((sum, value) => sum + value, 0) / available.length)
                : undefined;
            return acc;
        }, {} as Record<ValueCode, number | undefined>);

        return {
            periodStart: period.periodStart,
            periodEnd: period.periodEnd,
            label: formatStatisticsMonthLabel(period.periodStart),
            values
        };
    }

    private evaluate(reference: string, period: StatisticsPeriod): Promise<UpCareStat[]> {
        const cacheKey = [
            this.fhirBaseUrl,
            this.measureId,
            reference,
            period.periodStart,
            period.periodEnd
        ].join("|");

        const cached = evaluationCache.get(cacheKey);
        if (cached) return cached;

        const service = new ValueMeasureService({
            fhirBaseUrl: this.fhirBaseUrl,
            measureId: this.measureId,
            periodStart: period.periodStart,
            periodEnd: period.periodEnd
        });
        const promise = service.evaluate(reference).catch((error) => {
            evaluationCache.delete(cacheKey);
            throw error;
        });

        evaluationCache.set(cacheKey, promise);
        return promise;
    }

    private getPercentage(stats: UpCareStat[], code: ValueCode): number | undefined {
        const stat = stats.find((item) => item.id === STAT_IDS[code]);
        if (!stat || stat.rawValue === undefined || !Number.isFinite(stat.rawValue)) {
            return undefined;
        }

        if (stat.percent === undefined || !Number.isFinite(stat.percent)) {
            return Math.round(stat.rawValue * 10);
        }

        return Math.round(stat.percent);
    }

    private emptyPoint(period: StatisticsPeriod) {
        return {
            periodStart: period.periodStart,
            periodEnd: period.periodEnd,
            label: formatStatisticsMonthLabel(period.periodStart),
            values: VALUE_CODES.reduce((acc, code) => {
                acc[code] = undefined;
                return acc;
            }, {} as Record<ValueCode, number | undefined>)
        };
    }
}

export default ValueHistoryService;
